import styled from '@emotion/styled';
import { useMemo } from 'react';
import { useFavoritesModal } from '@/components/Approval/RightContainer/Favorites/useFavoritesModal';
import { useAtom } from 'jotai';
import { approvalFilter } from '@/recoil/atom';
import {
  ApprovalFilter,
  ApprovalType,
  FilterPassport,
  FilterStatus,
  PeriodStatus,
  SearchFields,
} from '@/types/api';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';

const MAX_LENGTH = 77;

const Favorites = () => {
  const [filters, setFilters] = useAtom(approvalFilter);
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const toOverPerson = (
    title: string,
    passports: FilterPassport[],
    allFlag?: boolean,
  ) => {
    if (passports.length === 0) return '';
    const all = isKorean ? '(전체)' : '(AND)';
    const or = isKorean ? '(일부)' : '(OR)';

    const flag = allFlag ? all : allFlag === false ? or : '';

    const [first, ...rest] = passports;
    return `${title}${flag}: ${first.name}${
      rest.length ? ` ${t('외')} ${rest.length}${t('명2')}` : ``
    }, `;
  };

  const toTypes = (types: ApprovalType[]) => {
    if (types.length === 0) return '';
    const [first, ...rest] = types;
    return `${t('양식')}: ${first.name}${
      rest.length ? ` ${t('외')} ${rest.length}${t('개')}` : ''
    }, `;
  };

  const toStatuses = (statuses: FilterStatus[]) => {
    if (statuses.length === 0) return '';
    const [first, ...rest] = statuses;
    return `${t('상태')}: ${first.name}${
      rest.length ? ` ${t('외')} ${rest.length}${t('개')}` : ''
    }, `;
  };

  const toPeriod = (periods: PeriodStatus[]) => {
    if (periods.length === 0) return '';
    const { type, name, value, value2 } = periods[0];
    return type === 3
      ? `${t('상신일')}: ${value}~${value2}, `
      : `${t('상신일')}: ${name}, `;
  };

  const toKeyword = ({
    keyword,
    searchFiled,
  }: {
    keyword: string;
    searchFiled: SearchFields;
  }) => {
    if (keyword.length === 0) return '';

    const subjectStr = `${t('제목')}: ${keyword}, `;
    const contentStr = `${t('내용')}: ${keyword}, `;

    switch (searchFiled) {
      case 'subject,content':
        return `${subjectStr} ${contentStr}`;
      case 'subject':
        return `${subjectStr}`;
      case 'content':
        return `${contentStr}`;
    }
  };

  const usePassportsToContent = (filters: ApprovalFilter) => {
    return useMemo(() => {
      const parts = [
        toKeyword({
          keyword: filters.keyword,
          searchFiled: filters.searchFields,
        }),
        toPeriod(filters.endAuthoredAt),
        toTypes(filters.types),
        toOverPerson(t('작성자'), filters.authorPassportIds),
        toOverPerson(
          t('결재자'),
          filters.approverPassportIds,
          filters.requireAllApprovers,
        ),
        toOverPerson(t('현재 결재자'), filters.currentApproverPassportIds),
        toOverPerson(t('최종 결재자'), filters.finalApproverPassportIds),
        toOverPerson(
          t('협조 결재자'),
          filters.coApproverPassportIds,
          filters.requireAllCoApprovers,
        ),
        toStatuses(filters.statuses),
      ];

      let result = parts
        .filter(Boolean)
        .join('')
        .replace(/[, ]+$/, '');

      if (result.length > MAX_LENGTH) {
        result = result.slice(0, MAX_LENGTH).replace(/[, ]+$/, '') + '...';
      }

      return result;
    }, [filters]);
  };

  const { openModal, FavoriteModal } = useFavoritesModal({
    defaultValue: usePassportsToContent(filters),
  });

  return (
    <>
      <StyledButton type={'button'} onClick={openModal}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            d="M10 4.5V15.5"
            stroke="#0066CC"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4.5 10H15.5"
            stroke="#0066CC"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div>{t('내 필터로 저장')}</div>
      </StyledButton>
      {FavoriteModal}
    </>
  );
};
export default Favorites;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  color: var(--dk-accent, #0066cc);
  font-size: 14px;
  line-height: 20px;
`;
